<template>
  <footer class="tw:border-t tw:border-gray-300">
    <div class="tw:bg-white">
      <div
        class="tw:max-w-(--tw-breakpoint-xl) tw:mx-auto tw:text-sm tw:p-4 tw:md:p-6 tw:md:pt-3 tw:flex tw:flex-col tw:md:flex-row tw:items-center tw:md:items-baseline"
        :class="
          isLanding
            ? 'tw:lg:max-w-(--tw-breakpoint-landing) tw:md:max-w-(--tw-breakpoint-md) tw:px-4 tw:sm:px-8 tw:md:px-0'
            : 'tw:max-w-(--tw-breakpoint-xl)'
        "
      >
        <div
          class="tw:md:mb-0 tw:w-full tw:grid tw:md:grid-cols-2 tw:md:gap-2 tw:text-gray-800 tw:text-center tw:md:text-left"
        >
          <BaseLink
            :href="getContactUrl(language, userId, selectedAccommodationId, userId ? isPaidClient : null)"
            target="_blank"
            class="link tw:cursor-pointer tw:hover:underline tw:hover:text-gray-300"
          >
            {{ $t('legal.contact') }}
          </BaseLink>
          <BaseLink
            :to="getTermsAndConditionsUrl()"
            target="_blank"
            class="link tw:cursor-pointer tw:hover:underline tw:hover:text-gray-300"
          >
            {{ $t('legal.termsAndConditions') }}
          </BaseLink>
          <BaseLink
            :href="getPrivacyPolicyUrl(language)"
            target="_blank"
            class="link tw:cursor-pointer tw:hover:underline tw:hover:text-gray-300"
          >
            {{ $t('legal.privacyPolicy') }}
          </BaseLink>
          <BaseLink
            to="/imprint"
            target="_blank"
            class="link tw:cursor-pointer tw:hover:underline tw:hover:text-gray-300"
          >
            {{ $t('legal.legalInformation') }}
          </BaseLink>
          <button
            class="link tw:md:text-left tw:cursor-pointer tw:hover:underline tw:hover:text-gray-300"
            @click="openCookieConsentForm"
          >
            {{ $t('cookie_preferences') }}
          </button>
        </div>
        <div class="tw:flex tw:flex-row tw:shrink-0">
          <CurrencySelector
            v-show="isCurrencyEditable"
            size="big"
            version="footer"
            :isLanding="isLanding"
            currencySelectTestId="footer-currency-selector"
          />
          <LanguageSelector size="big" version="footer" :isLanding="isLanding" />
        </div>
      </div>
    </div>
    <div class="tw:mx-auto tw:h-[0px] tw:lg:h-[1px] tw:max-w-(--tw-breakpoint-xl) tw:text-center tw:bg-purple-300" />
    <div class="tw:bg-gray-800">
      <div
        class="tw:max-w-(--tw-breakpoint-xl) tw:w-full tw:pt-8 tw:pb-5 tw:mx-auto tw:flex tw:flex-col tw:space-between tw:items-center tw:px-4"
      >
        <div class="tw:text-center">
          <TrivagoWhite class="tw:mx-auto tw:w-[102px] tw:h-[32px]" />
        </div>
        <div class="tw:flex tw:items-center tw:mt-4 tw:flex-col tw:md:flex-row">
          <small
            class="tw:block tw:text-xs tw:text-gray-500 tw:text-sm tw:mr-4 tw:mt-4 tw:md:mt-0 tw:md:order-0 tw:order-1"
          >
            {{ $t('legal.brand.copyright', { year: currentYear }) }}
            | {{ $t('legal.brand.rights') }}
          </small>
          <address
            class="tw:m-0 tw:pt-0 tw:pb-0 tw:text-sm tw:text-gray-500 tw:md:order-1 tw:order-0 tw:text-center tw:sm:text-left"
          >
            trivago N.V. , Kesselstraße 5 - 7, 40221 Düsseldorf, Germany.
          </address>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'

import BaseLink from '@/components/BaseLink.vue'
import CurrencySelector from '@/components/locale/CurrencySelector.vue'
import LanguageSelector from '@/components/locale/LanguageSelector.vue'
import TrivagoWhite from '@/components/icons/TrivagoWhite.svg?component'

import { getPrivacyPolicyUrl, getContactUrl } from '@/utils/Utility.ts'
import { selectedAccommodationId } from '@/layouts/queries'
import { useCampaign } from '@/components/rateConnect/queries'
import { useStudioPlusCampaign } from '../payment/queries'
import { useSessionValues } from '@/composables/useSessionValues'

const route = useRoute()

defineProps({
  isLanding: {
    type: Boolean,
    default: false,
  },
})

const { campaignsData } = useCampaign(selectedAccommodationId)
const { studioPlusCampaign } = useStudioPlusCampaign(selectedAccommodationId)
const isPaidClient = computed(
  () =>
    Boolean(studioPlusCampaign.value) ||
    (Array.isArray(campaignsData.value) ? campaignsData.value.length > 0 : Boolean(campaignsData.value))
)
const { isCurrencyEditable, language, userId } = useSessionValues()
const currentYear = computed(() => {
  const localYear = new Date().getFullYear()
  return localYear >= 2019 ? localYear : 2019
})

function getTermsAndConditionsUrl() {
  if (route.path.includes('rate-connect')) {
    return '/terms-and-conditions?app=rate-connect'
  } else {
    return '/terms-and-conditions'
  }
}

function openCookieConsentForm() {
  window.UC_UI.showSecondLayer()
}
</script>

<style scoped>
.link {
  flex: 25% 0 0;
  @media (max-width: 768px) {
    margin: 0.75rem 1rem;
    flex: calc(50% - 32px) 0 0;
  }
}
</style>
